import * as THREE from 'three';

/**
 * A model's contact shadow: dark where it touches the ground, soft round it, drawn once from its
 * own geometry when it is fitted. For a floor that shows no sun shadow -- a ground-projected 360°
 * photo, unlit (`colour #000000`, the photo as its glow) -- where the round blob alone left a
 * marsrover hovering: its wheels at the blob's faint edge (Pieter, on a Quest 3, Oct 2026).
 *
 * Every triangle of the model that reaches down into the lowest `CONTACT.low` of its height, as its
 * skeleton poses it now, is laid flat on a grid over its footprint, the lower the darker; the grid is
 * blurred. Two layers: the touch patches (wheels, feet, a low belly) and a faint oval under the
 * whole footprint, so a model `above` the land (a swimmer, a flyer) keeps only the oval, fading.
 * A clip that moves the feet (a walk, a rear-up) keeps the patches of the pose it was drawn in.
 */
export const CONTACT = {
  /** Texels a side. */
  size: 64,
  /** Of the model's height (at least `lowMin` m): what counts as touching the ground. */
  low: 0.15,
  lowMin: 0.05,
  /** Of the footprint's larger side, beyond it on every side: room for the soft edge. */
  margin: 0.18,
  /** Darkness where it touches, and of the faint oval under the whole footprint. */
  touch: 0.85,
  shade: 0.45,
  /** How much a texel half-touched counts (a wheel's narrow tread, blurred). */
  gain: 2.2,
  /** The blur's reach, as a share of the margin. */
  blur: 0.7,
  /** Metres above the model's feet (the blob's height: above a photo floor's 0.012). */
  y: 0.03,
  /**
   * At most this many skinned vertices are posed on the CPU; a bigger skinned model is sampled,
   * every n-th vertex laid down as a point instead of its triangles (viewer-scene's `max`). Meshes
   * without a skeleton cost a read a vertex and are taken whole.
   */
  vertices: 20_000,
  /** Metres above the land by which the touch patches, and then the oval, have faded out. */
  touchFade: 0.5,
  ovalFade: 3,
} as const;

export type ContactShadow = {
  /** What lies flat under the model, in the holder's space: the two layers in it. */
  readonly mesh: THREE.Group;
  readonly touch: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  readonly oval: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  /** How high above the land the model rides (ADR-054's `above`), metres: the layers fade. */
  lift(metres: number): void;
  dispose(): void;
};

type Flat = { x: number; z: number; w: number }[];

/**
 * The model's low parts in `frame`'s space, laid flat: triangles (corners, and how low, 0–1, 1 = on
 * the ground), or single points from skinned meshes beyond CONTACT.vertices vertices. Each vertex as its
 * skeleton poses it (`getVertexPosition`), once.
 */
function lowParts(root: THREE.Object3D, frame: THREE.Object3D, low: number): Flat[] {
  frame.updateMatrixWorld(true);
  const into = frame.matrixWorld.clone().invert();
  const meshes: THREE.Mesh[] = [];
  let total = 0;
  root.traverse((node) => {
    if (!(node instanceof THREE.Mesh) || !node.geometry) return;
    const position = (node.geometry as THREE.BufferGeometry).getAttribute('position');
    if (!position) return;
    meshes.push(node as THREE.Mesh);
    if (node instanceof THREE.SkinnedMesh) {
      node.skeleton.update();
      total += position.count;
    }
  });
  const stride = Math.max(1, Math.ceil(total / CONTACT.vertices));
  const found: Flat[] = [];
  const m = new THREE.Matrix4();
  const v = new THREE.Vector3();
  const weight = (y: number): number => 1 - Math.max(0, y) / low;
  for (const mesh of meshes) {
    const geometry = mesh.geometry;
    const count = geometry.getAttribute('position').count;
    m.multiplyMatrices(into, mesh.matrixWorld);
    if (stride > 1 && mesh instanceof THREE.SkinnedMesh) {
      for (let i = 0; i < count; i += stride) {
        mesh.getVertexPosition(i, v).applyMatrix4(m);
        if (v.y < low && Number.isFinite(v.x + v.z))
          found.push([{ x: v.x, z: v.z, w: weight(v.y) }]);
      }
      continue;
    }
    const posed = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      mesh.getVertexPosition(i, v).applyMatrix4(m);
      posed[i * 3] = v.x;
      posed[i * 3 + 1] = v.y;
      posed[i * 3 + 2] = v.z;
    }
    const index = geometry.getIndex();
    const corners = index ? index.count : count;
    for (let i = 0; i + 2 < corners; i += 3) {
      const k = [0, 1, 2].map((d) => (index ? index.getX(i + d) : i + d) * 3);
      const bottom = Math.min(posed[k[0]! + 1]!, posed[k[1]! + 1]!, posed[k[2]! + 1]!);
      if (!(bottom < low)) continue;
      const w = weight(bottom);
      const flat = k.map((o) => ({ x: posed[o]!, z: posed[o + 2]!, w }));
      if (flat.every((p) => Number.isFinite(p.x + p.z))) found.push(flat);
    }
  }
  return found;
}

/** A box blur along rows then columns, `r` texels, twice: nearly a Gaussian. */
function blur(grid: Float32Array, n: number, r: number): void {
  if (r < 1) return;
  const tmp = new Float32Array(n * n);
  for (let pass = 0; pass < 2; pass++) {
    for (let y = 0; y < n; y++) {
      for (let x = 0; x < n; x++) {
        let sum = 0;
        for (let k = -r; k <= r; k++) {
          const xx = Math.min(n - 1, Math.max(0, x + k));
          sum += grid[y * n + xx]!;
        }
        tmp[y * n + x] = sum / (2 * r + 1);
      }
    }
    for (let y = 0; y < n; y++) {
      for (let x = 0; x < n; x++) {
        let sum = 0;
        for (let k = -r; k <= r; k++) {
          const yy = Math.min(n - 1, Math.max(0, y + k));
          sum += tmp[yy * n + x]!;
        }
        grid[y * n + x] = sum / (2 * r + 1);
      }
    }
  }
}

/**
 * The contact shadow of the model under `root`, in `frame`'s space (the holder's: the model frame
 * at scale 1, feet at y = 0), `height` metres tall there. Undefined when nothing of it is low (a
 * model with no geometry): the round blob is all it gets.
 */
export function contactShadow(
  root: THREE.Object3D,
  frame: THREE.Object3D,
  height: number,
): ContactShadow | undefined {
  const low = Math.max(CONTACT.lowMin, CONTACT.low * height);
  const triangles = lowParts(root, frame, low);
  if (triangles.length === 0) return undefined;
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const t of triangles) {
    for (const p of t) {
      minX = Math.min(minX, p.x);
      maxX = Math.max(maxX, p.x);
      minZ = Math.min(minZ, p.z);
      maxZ = Math.max(maxZ, p.z);
    }
  }
  // The footprint's oval from the whole model's lowest part, and room round it for the soft edge.
  const margin = Math.max(0.05, CONTACT.margin * Math.max(maxX - minX, maxZ - minZ));
  const x0 = minX - margin;
  const z0 = minZ - margin;
  const width = maxX - minX + 2 * margin;
  const depth = maxZ - minZ + 2 * margin;
  const n = CONTACT.size;
  const grid = new Float32Array(n * n);
  // Texel (i, j): x = x0 + (i + 0.5) / n * width; row j runs from the far side (−z) at j = n − 1,
  // as the plane lies (its +y turned to −z).
  const column = (x: number): number => ((x - x0) / width) * n - 0.5;
  const row = (z: number): number => ((z0 + depth - z) / depth) * n - 0.5;
  for (const part of triangles) {
    const a = part[0]!;
    const b = part[1] ?? a;
    const c = part[2] ?? a;
    const ax = column(a.x);
    const ay = row(a.z);
    const bx = column(b.x);
    const by = row(b.z);
    const cx = column(c.x);
    const cy = row(c.z);
    const w = Math.max(a.w, b.w, c.w);
    // Sampled at least once a texel along each edge and across: slivers seen edge-on (a wheel's
    // side) mark the texels along them too.
    const steps = Math.ceil(
      Math.max(
        Math.hypot(bx - ax, by - ay),
        Math.hypot(cx - ax, cy - ay),
        Math.hypot(cx - bx, cy - by),
      ),
    );
    for (let s = 0; s <= steps; s++) {
      for (let t = 0; t <= steps - s; t++) {
        const i = Math.round(ax + ((bx - ax) * s + (cx - ax) * t) / Math.max(1, steps));
        const j = Math.round(ay + ((by - ay) * s + (cy - ay) * t) / Math.max(1, steps));
        if (i < 0 || j < 0 || i >= n || j >= n) continue;
        if (w > grid[j * n + i]!) grid[j * n + i] = w;
      }
    }
  }
  blur(
    grid,
    n,
    Math.max(1, Math.round(((CONTACT.blur * margin) / Math.max(width, depth)) * n * 0.5)),
  );
  const touchData = new Uint8Array(n * n * 4);
  const ovalData = new Uint8Array(n * n * 4);
  for (let j = 0; j < n; j++) {
    for (let i = 0; i < n; i++) {
      const u = ((i + 0.5) / n) * 2 - 1;
      const v = ((j + 0.5) / n) * 2 - 1;
      const oval = Math.max(0, 1 - (u * u + v * v)) ** 1.5;
      const touch = Math.min(1, grid[j * n + i]! * CONTACT.gain);
      touchData[(j * n + i) * 4 + 3] = Math.round(CONTACT.touch * touch * 255);
      ovalData[(j * n + i) * 4 + 3] = Math.round(CONTACT.shade * oval * 255);
    }
  }
  const layer = (data: Uint8Array, name: string) => {
    const texture = new THREE.DataTexture(data, n, n, THREE.RGBAFormat);
    texture.magFilter = THREE.LinearFilter;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.generateMipmaps = true;
    texture.needsUpdate = true;
    const plane = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({
        color: 0x000000,
        map: texture,
        transparent: true,
        depthWrite: false,
        // Over a floor a centimetre or two below it, at any distance.
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -2,
      }),
    );
    plane.name = name;
    // After a world's see-through floors (renderOrder 0), as the blob.
    plane.renderOrder = 1;
    plane.raycast = () => {};
    return plane;
  };
  const oval = layer(ovalData, 'contact-oval');
  const touch = layer(touchData, 'contact-touch');
  const mesh = new THREE.Group();
  mesh.name = 'contact';
  mesh.add(oval, touch);
  mesh.rotation.x = -Math.PI / 2;
  mesh.scale.set(width, depth, 1);
  mesh.position.set(x0 + width / 2, CONTACT.y, z0 + depth / 2);
  mesh.visible = false;
  return {
    mesh,
    touch,
    oval,
    lift(metres) {
      const up = Math.max(0, metres);
      touch.material.opacity = Math.max(0, 1 - up / CONTACT.touchFade);
      oval.material.opacity = Math.max(0, 1 - up / CONTACT.ovalFade);
      touch.visible = touch.material.opacity > 0;
      oval.visible = oval.material.opacity > 0;
    },
    dispose() {
      for (const plane of [touch, oval]) {
        plane.material.map?.dispose();
        plane.geometry.dispose();
        plane.material.dispose();
      }
    },
  };
}
